// doc-nav.ts — WHOLE-DOCUMENT NAVIGATION: a table of contents over ONE long
// scrolling document, where a TOC click is an animated scroll to that part
// and the row for the part being READ is highlighted as the reader scrolls
// (scroll-spy). One implementation, every consumer (#810).
//
// WHY A MODULE AND NOT A RECIPE. The mechanism underneath — the band decision
// that says which part is "the one you are reading", the rAF coalescing, the
// click-driven SELECTION LOCK that stops the highlight flicking through every
// intermediate part during a smooth scroll, the scrollend release, the
// safety timer and the reduced-motion arm — is `attachScrollSpy`
// (feedback-nav.ts), and primitives-catalog arm 6 pins the hand-rolled
// alternative (an IntersectionObserver spy) at ZERO. What every consumer then
// wrote for itself was the SAME thirty lines on top: measure the parts' tops,
// find the TOC row for an id, stamp `aria-current` + a class on it, unstamp
// the previous one, and turn a click into `navigateTo(id, top - offset)`.
// Two copies of that is how the highlight rule forks (one surface stamps
// aria-current, the next forgets), so this is the one copy. The
// primitives-nav gate's `doc-nav` arm counts the consumers BY FILE and
// refuses a second `attachScrollSpy(` caller or a hand-rolled
// `behavior: 'smooth'` anywhere on the five surfaces.
//
// WHAT STAYS WITH THE CONSUMER: where the parts are (`anchors()`), which DOM
// row answers for an id (`rowFor`), and any extra it paints on activation
// (`onActive` — a sliding indicator pill, a kicker). Nothing here knows what
// a TOC looks like.
//
// DOM-free enough for the smoke's stub document: every browser touch goes
// through the elements the consumer hands in (offsetTop, classList,
// setAttribute) and through attachScrollSpy's own guarded rAF/scrollTo.

import { attachScrollSpy, type ScrollSpyHandle } from './feedback-nav.js';

export interface DocNavAnchor {
  /** The part's id — the TOC row's key and the anchor a deep link names. */
  id: string;
  /** The part's element inside the scroller (offsetTop is measured live). */
  el: HTMLElement;
}

export interface DocNavOpts {
  /** The scrolling pane the document lives in. */
  scroller: HTMLElement;
  /** Measured fresh on every sweep — parts appear, re-order and re-render. */
  anchors: () => readonly DocNavAnchor[];
  /** The TOC row for a part id, or null/undefined when that row is not in
   *  the DOM right now (a collapsed group). */
  rowFor: (id: string) => HTMLElement | null | undefined;
  /** Fires after the highlight moved (also once at mount, with the first
   *  measured id). The consumer's extras only — the row is already stamped. */
  onActive?: (id: string | null) => void;
  /** px kept above the part when a click lands the scroll (default 6). */
  offset?: number;
  /** ms before a smooth-scroll lock releases itself (spy default 1500). */
  lockMs?: number;
  /** The class stamped beside `aria-current="true"` (default 'on'). */
  activeCls?: string;
}

export interface DocNavHandle {
  /** Click-to-scroll: select the part NOW (locked through the animation),
   *  then scroll to it — instantly under reduced motion. False when no part
   *  carries that id (the caller decides whether that is an error). */
  go(id: string): boolean;
  /** Re-measure now (a part grew, the pane resized). Returns the active id. */
  sweep(): string | null;
  /** The part currently marked as being read. */
  active(): string | null;
  /** The TOC rows were re-rendered — re-stamp the highlight on the new row
   *  for the same id (the spy state itself is untouched). */
  repaint(): void;
  destroy(): void;
}

/** The attribute the active TOC row carries — ONE spelling for every
 *  consumer, so a style rule and a probe can both find "the row being read"
 *  without knowing whose TOC it is. */
export const DOC_NAV_CURRENT_ATTR = 'aria-current';
export const DOC_NAV_CURRENT_VALUE = 'true';

/** The app's own reduce switch — `<html data-reduce="1">` (effects-seat). */
export function appReduceSeat(): boolean {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement as { getAttribute?: (k: string) => string | null } | null;
  return root?.getAttribute?.('data-reduce') === '1';
}

export function mountDocNav(opts: DocNavOpts): DocNavHandle {
  const activeCls = opts.activeCls ?? 'on';
  const offset = opts.offset ?? 6;
  let current: string | null = null;
  let stamped: HTMLElement | null = null;

  const paint = (id: string | null): void => {
    if (stamped) {
      stamped.classList.remove(activeCls);
      stamped.removeAttribute(DOC_NAV_CURRENT_ATTR);
      stamped = null;
    }
    if (!id) return;
    const row = opts.rowFor(id);
    if (!row) return;
    row.classList.add(activeCls);
    row.setAttribute(DOC_NAV_CURRENT_ATTR, DOC_NAV_CURRENT_VALUE);
    stamped = row;
  };

  const spy: ScrollSpyHandle = attachScrollSpy({
    scroller: opts.scroller,
    sections: () => opts.anchors().map((a) => ({ id: a.id, top: a.el.offsetTop })),
    onActive: (id) => {
      current = id;
      paint(id);
      opts.onActive?.(id);
    },
    ...(opts.lockMs !== undefined ? { lockMs: opts.lockMs } : {}),
  });

  return {
    go(id: string): boolean {
      const a = opts.anchors().find((x) => x.id === id);
      if (!a) return false;
      // BOTH reduce lanes (the disclosure primitive's rule): the OS's
      // prefers-reduced-motion is the spy's own read; the app's `[data-reduce="1"]`
      // seat is a document attribute matchMedia cannot see, so it is read here
      // and handed down as `instant`.
      spy.navigateTo(id, a.el.offsetTop - offset, { instant: appReduceSeat() });
      return true;
    },
    sweep: () => spy.sweep(),
    active: () => current,
    repaint: () => paint(current),
    destroy(): void {
      paint(null);
      spy.destroy();
    },
  };
}
